import React, { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Autocomplete, Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { useAppContext } from '../../contexts/AppContext';
import { InfoGrid, PaneelKop, Sectie } from './bouwstenen';
import { ROLLEN } from '../../utils/rollen';

export default function GemeenteSidebar() {
  const { selectedGemeente, setSelectedGemeente, fetchWithAuth, addToast } = useAppContext();
  const [contacten, setContacten] = useState([]);
  const [geregistreerden, setGeregistreerden] = useState([]);
  const [nieuw, setNieuw] = useState({ naam: '', functie: '', email: '' });
  const [bezig, setBezig] = useState(false);

  const gemeentecode = selectedGemeente?.gemeentecode;
  const laadContacten = useCallback(async () => {
    if (!gemeentecode) return;
    try {
      const res = await fetchWithAuth(`/api/gemeenten/${encodeURIComponent(gemeentecode)}/contacten`);
      const data = await res.json();
      if (data.success) {
        setContacten(data.contacten);
        setGeregistreerden(data.geregistreerden || []);
      }
    } catch (e) {
      console.warn('Contacten laden mislukt:', e);
    }
  }, [gemeentecode, fetchWithAuth]);

  useEffect(() => { laadContacten(); }, [laadContacten]);

  if (!selectedGemeente) return null;

  const voegToe = async () => {
    setBezig(true);
    try {
      const res = await fetchWithAuth(`/api/gemeenten/${encodeURIComponent(gemeentecode)}/contacten`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nieuw),
      });
      const data = await res.json();
      if (data.success) {
        setNieuw({ naam: '', functie: '', email: '' });
        laadContacten();
      } else {
        addToast(data.error || 'Toevoegen mislukt', 'error');
      }
    } catch (e) {
      addToast('Toevoegen mislukt', 'error');
    } finally {
      setBezig(false);
    }
  };

  const verwijder = async (id) => {
    try {
      const res = await fetchWithAuth(
        `/api/gemeenten/${encodeURIComponent(gemeentecode)}/contacten/${id}`,
        { method: 'DELETE' },
      );
      const data = await res.json();
      if (data.success) laadContacten();
      else addToast(data.error || 'Verwijderen mislukt', 'error');
    } catch (e) {
      addToast('Verwijderen mislukt', 'error');
    }
  };

  return (
    <>
      <PaneelKop
        label="Gemeente"
        titel={selectedGemeente.gemeentenaam}
        subtitel={selectedGemeente.regio}
        onSluit={() => setSelectedGemeente(null)}
        offsetTop={44}
      />
      <Stack gap="md" p="md">
        <InfoGrid items={[
          { label: 'Gemeente', waarde: selectedGemeente.gemeentenaam },
          { label: 'Regio', waarde: selectedGemeente.regio },
        ]} />

        <Sectie titel="Betrokken ambtenaren">
          {geregistreerden.length === 0 && contacten.length === 0 && (
            <Text size="xs" c="dimmed">Nog geen contactpersonen vastgelegd.</Text>
          )}
          {/* Eén regel per persoon: naam · functie. De e-mail zit in de tooltip
              zodat de regel kort blijft. Geregistreerden hebben geen ✕. */}
          {geregistreerden.map((g, i) => (
            <Group key={`g${i}`} justify="space-between" wrap="nowrap" gap={4}
              style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', paddingBottom: 2 }}>
              <Text size="sm" truncate style={{ flex: 1, minWidth: 0 }}
                title={[g.naam, g.functie, g.email].filter(Boolean).join(' · ')}>
                <span style={{ fontWeight: 500 }}>{g.naam}</span>
                {g.functie ? <span style={{ color: 'var(--mantine-color-dimmed)' }}> · {g.functie}</span> : null}
              </Text>
            </Group>
          ))}
          {contacten.map(c => (
            <Group key={c.id} justify="space-between" wrap="nowrap" gap={4}
              style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', paddingBottom: 2 }}>
              <Text size="sm" truncate style={{ flex: 1, minWidth: 0 }}
                title={[c.naam, c.functie, c.email].filter(Boolean).join(' · ')}>
                <span style={{ fontWeight: 500 }}>{c.naam}</span>
                {c.functie ? <span style={{ color: 'var(--mantine-color-dimmed)' }}> · {c.functie}</span> : null}
              </Text>
              <ActionIcon variant="subtle" color="red" size="sm"
                aria-label={`Verwijder ${c.naam}`} onClick={() => verwijder(c.id)}>
                ✕
              </ActionIcon>
            </Group>
          ))}

          <Stack gap={6} mt={4}>
            <Group gap={6} grow>
              <TextInput size="xs" placeholder="Naam" value={nieuw.naam}
                onChange={e => { const w = e.currentTarget.value; setNieuw(n => ({ ...n, naam: w })); }} />
              <Autocomplete size="xs" placeholder="Functie" value={nieuw.functie}
                data={ROLLEN}
                onChange={w => setNieuw(n => ({ ...n, functie: w }))} />
            </Group>
            <Group gap={6} wrap="nowrap">
              <TextInput size="xs" placeholder="E-mail (optioneel)" value={nieuw.email}
                style={{ flex: 1 }}
                onChange={e => { const w = e.currentTarget.value; setNieuw(n => ({ ...n, email: w })); }} />
              <Button size="compact-xs" onClick={voegToe} loading={bezig}
                disabled={!nieuw.naam.trim()}>
                Toevoegen
              </Button>
            </Group>
          </Stack>
        </Sectie>

        <Text size="sm" c="dimmed">
          Dubbelklik op een gekleurde buurt om de buurtaanpak te openen. De kleur
          geeft het geplande jaar van uitvoering aan (zie de legenda rechtsonder).
        </Text>
      </Stack>
    </>
  );
}
